import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, IconButton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Apps,
  BarChart as BarChartIcon,
  CloudUpload as CloudUploadIcon,
  Crop,
  FolderSpecial,
  Palette, 
  PhotoLibrary,
} from '@mui/icons-material';
import { isStaffUser } from '../../utils/staffUser';
import { RootState } from '../../store';
import { useI18n } from '../../i18n/useI18n';
import UserPhotos from './Photos';
import UserChannelCustomFrames from './UserChannelCustomFrames';
import UserCollections from './Collections';
import UserIcons from './Icons';
import ThemeList from '../Theme/ThemeList';
import MPhotoSectionTabs from '../shared/MPhotoSectionTabs';
import GalleryStatsPanel from '../shared/GalleryStatsPanel';
import UserPageShell from '../shared/UserPageShell';

type ProTab = 'photos' | 'custom-frames' | 'themes' | 'collections' | 'icons' | 'stats';

const PRO_WARM = '#ff8ba7';
const PRO_PINK_DEEP = '#e86a8a';

/** Photos.tsx lắng nghe event này để mở file picker trên mobile */
export const PRO_PHOTOS_OPEN_UPLOAD = 'mphoto-pro-open-upload';

const PhotosHub: React.FC = () => {
  const { pages: t } = useI18n();
  const { user } = useSelector((state: RootState) => state.auth);
  const hasProAccess = user?.deviceAccess?.pro ?? true;
  const readOnly = isStaffUser(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('proTab');
  const rawTab: ProTab =
    tabParam === 'custom-frames'
      ? 'custom-frames'
      : tabParam === 'themes'
        ? 'themes'
        : tabParam === 'collections'
          ? 'collections'
          : tabParam === 'icons'
            ? 'icons'
            : tabParam === 'stats'
              ? 'stats'
              : 'photos';
  const activeTab: ProTab =
    readOnly && rawTab !== 'photos' && rawTab !== 'collections' ? 'photos' : rawTab;

  const setActiveTab = (tab: ProTab) => {
    if (tab === 'photos') {
      searchParams.delete('proTab');
      setSearchParams(searchParams, { replace: true });
    } else {
      searchParams.set('proTab', tab);
      setSearchParams(searchParams, { replace: true });
    }
  };

  const tabOptions = useMemo(
    () => {
      const opts = [
        { value: 'photos' as const, label: t.mPhotoPro.tabs.photos, icon: <PhotoLibrary fontSize="inherit" /> },
        {
          value: 'collections' as const,
          label: t.mPhotoPro.tabs.collections,
          icon: <FolderSpecial fontSize="inherit" />,
        },
      ];
      if (readOnly) return opts;
      return [
        { value: 'photos' as const, label: t.mPhotoPro.tabs.photos, icon: <PhotoLibrary fontSize="inherit" /> },
        { value: 'custom-frames' as const, label: t.mPhotoPro.tabs.customFrames, shortLabel: t.mPhotoPro.tabs.customFramesShort, icon: <Crop fontSize="inherit" /> },
        { value: 'themes' as const, label: t.mPhotoPro.tabs.themes, shortLabel: t.mPhotoPro.tabs.themesShort, icon: <Palette fontSize="inherit" /> },
        {
          value: 'collections' as const,
          label: t.mPhotoPro.tabs.collections,
          icon: <FolderSpecial fontSize="inherit" />,
        },
        { value: 'icons' as const, label: t.mPhotoPro.tabs.icons, icon: <Apps fontSize="inherit" /> },
        { value: 'stats' as const, label: t.mPhotoPro.tabs.stats, icon: <BarChartIcon fontSize="inherit" /> },
      ];
    },
    [t, readOnly],
  );

  if (!hasProAccess) {
    return (
      <UserPageShell enter>
        <Typography color="text.secondary">{t.mPhotoPro.noAccess}</Typography>
      </UserPageShell>
    );
  }

  return (
    <UserPageShell enter>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 36,
          mb: { xs: 1.5, md: 2.5 },
        }}
      >
        <Typography
          variant="h5"
          component="h1"
          sx={{
            textAlign: 'center',
            fontWeight: 800,
            fontSize: { xs: '1.35rem', sm: '1.75rem' },
            lineHeight: 1.2,
            color: PRO_PINK_DEEP,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          PRO
        </Typography>

        {activeTab === 'photos' && !readOnly && (
          <IconButton
            aria-label={t.photos.uploadCta}
            onClick={() => window.dispatchEvent(new Event(PRO_PHOTOS_OPEN_UPLOAD))}
            sx={{
              display: { xs: 'inline-flex', sm: 'none' },
              position: 'absolute',
              right: 0,
              width: 36,
              height: 36,
              borderRadius: 2,
              color: PRO_PINK_DEEP,
              border: '1px solid',
              borderColor: alpha(PRO_PINK_DEEP, 0.45),
              bgcolor: alpha(PRO_WARM, 0.22),
              '&:hover': {
                bgcolor: alpha(PRO_WARM, 0.35),
                borderColor: PRO_PINK_DEEP,
              },
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Box>

      <MPhotoSectionTabs
        value={activeTab}
        onChange={setActiveTab}
        options={tabOptions}
        accentColor={PRO_PINK_DEEP}
        size="sm"
        sticky
        stickyTop={{ xs: 56, sm: 64 }}
      />

      {activeTab === 'photos' && <UserPhotos />}
      {!readOnly && activeTab === 'custom-frames' && <UserChannelCustomFrames channel="pro" />}
      {!readOnly && activeTab === 'themes' && <ThemeList channel="pro" embedded />}
      {activeTab === 'collections' && <UserCollections />}
      {!readOnly && activeTab === 'icons' && <UserIcons channel="pro" embedded />}
      {!readOnly && activeTab === 'stats' && <GalleryStatsPanel channel="pro" accentColor={PRO_PINK_DEEP} />}
    </UserPageShell>
  );
};

export default PhotosHub;
